import { useLanguage } from '../../shell/language-context';

// A pickable place: a registry city or one of its areas.
export interface PlaceOption {
  id: string;
  slug: string;
  label: string;
  // The city an area belongs to; empty for a city row.
  cityLabel: string;
  isCity: boolean;
}

// Hero panel 2 (design DC:359-387): "Current location", a filter box, the visitor's
// recent picks, "All of {region}", then every city with its areas indented under it.
export function HeroLocationPanel({
  places,
  recent,
  filter,
  onFilter,
  onPick,
  onClear,
}: {
  places: PlaceOption[];
  recent: PlaceOption[];
  filter: string;
  onFilter: (value: string) => void;
  onPick: (place: PlaceOption) => void;
  onClear: () => void;
}) {
  const { t } = useLanguage();
  const q = filter.trim().toLowerCase();
  // A city stays when it or any of its areas match; an area stays when it or its city
  // matches — so typing a city name lists its whole neighbourhood set.
  const shown = places.filter((p) => {
    if (!q) return true;
    if (p.isCity) {
      return places.some((a) => (a.cityLabel === p.label || a.id === p.id) && a.label.toLowerCase().includes(q));
    }
    return p.label.toLowerCase().includes(q) || p.cityLabel.toLowerCase().includes(q);
  });

  return (
    <div className="hm-panel hm-panel--loc">
      <button type="button" className="hm-loc__current" onMouseDown={(e) => e.preventDefault()} onClick={onClear}>
        <span className="hm-loc__current-ic" aria-hidden="true">
          ◎
        </span>
        <span>
          <span className="hm-loc__current-title">{t('dc50.loc.current')}</span>
          <span className="hm-loc__current-sub">{t('dc50.loc.current_sub')}</span>
        </span>
      </button>
      <input
        className="hm-loc__filter"
        value={filter}
        onChange={(e) => onFilter(e.target.value)}
        placeholder={t('dc50.loc.filter_ph')}
        aria-label={t('dc50.loc.filter_ph')}
      />
      {recent.length > 0 ? (
        <>
          <div className="hm-panel__head">{t('dc50.loc.recent')}</div>
          {recent.map((p) => (
            <button
              key={`r-${p.id}`}
              type="button"
              className="hm-loc__recent"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onPick(p)}
            >
              <span className="hm-loc__recent-ic" aria-hidden="true">
                ↻
              </span>
              <span className="hm-loc__recent-label">{p.isCity ? p.label : `${p.label}, ${p.cityLabel}`}</span>
            </button>
          ))}
        </>
      ) : null}
      <button type="button" className="hm-loc__all" onMouseDown={(e) => e.preventDefault()} onClick={onClear}>
        {t('dc50.loc.all')}
      </button>
      {shown.map((p) => (
        <button
          key={p.id}
          type="button"
          className={p.isCity ? 'hm-loc__row' : 'hm-loc__row hm-loc__row--area'}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onPick(p)}
        >
          <span className={p.isCity ? 'hm-loc__city' : 'hm-loc__area'}>{p.label}</span>
          <span className="hm-loc__sub">{p.isCity ? t('dc50.loc.city') : p.cityLabel}</span>
        </button>
      ))}
    </div>
  );
}
